import React, { FC } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  TextField,
  InputAdornment,
  FormHelperText,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

import { COLORS, InputFieldWrapper, TYPOGRAPHY_COMMON_STYLES } from "components";
import { useNotify } from "hooks";
import { QUERY_KEYS } from "consts";
import { checkNullValue, getDateFormatset } from "utils";
import { addRecDataBySite, editRecDataBySite } from "api/rec";
import { SiteByIdDTO } from "api";

interface FormValues {
  utilityProvider: string;
  utilityTypeName: string;
  country: string;
  renewableEnergy: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
}

interface RecModalProps {
  isOpen: boolean;
  onClose: () => void;
  allowedValues?: { countries: string[]; utilityTypes: { id: number; name: string }[] };
  columns?: any;
  site?: SiteByIdDTO;
  editREC?: any;
}

export const RecModal: FC<RecModalProps> = ({
  isOpen,
  onClose,
  allowedValues,
  columns,
  site,
  editREC,
}) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { state } = useLocation();

  const recData = state?.collector;

  const { countries = [], utilityTypes = [] } = allowedValues || {};
  const countriesAscending = countries.sort();
  const defaultValues: FormValues = editREC
    ? {
      utilityProvider: editREC.utilityProvider || "",
      utilityTypeName: editREC.utilityTypeName || "",
      country: editREC.country || "",
      renewableEnergy: editREC.renewableEnergy || "",
      startDate: editREC.startDate ? dayjs(editREC.startDate) : null,
      endDate: editREC.endDate ? dayjs(editREC.endDate) : null,
    }
    : {
      utilityProvider: recData?.providerName || "",
      utilityTypeName: recData?.utilityTypeName || "",
      country: recData?.country || "",
      renewableEnergy: "",
      startDate: null,
      endDate: null,
    };

  const { control, handleSubmit, formState: { isValid } } = useForm<FormValues>({
    mode: "onChange",
    defaultValues,
    resolver: async (data) => {
      const errors: any = {};

      // Required validations for all fields
      if (!data.utilityProvider) {
        errors.utilityProvider = { message: "Input is required" };
      }
      if (!data.utilityTypeName) {
        errors.utilityTypeName = { message: "Input is required" };
      }
      if (!data.country) {
        errors.country = { message: "Input is required" };
      }
      if (!data.renewableEnergy) {
        errors.renewableEnergy = { message: "Input is required" };
      } else {
        const renewableValue = Number(data.renewableEnergy);
        if (Number.isNaN(renewableValue)) {
          errors.renewableEnergy = { message: "Input must be a numerical value." };
        } else if (renewableValue < 0 || renewableValue > 100) {
          errors.renewableEnergy = { message: "Please enter a percentage between 0 and 100." };
        }
      }
      if (!data.startDate) {
        errors.startDate = { message: "Input is required" };
      }
      if (!data.endDate) {
        errors.endDate = { message: "Input is required" };
      }

      return {
        values: Object.keys(errors).length ? {} : data,
        errors,
      };
    },
  });

  const { mutateAsync: addRecDocument } = useMutation(addRecDataBySite);
  const { mutateAsync: editRecDocument } = useMutation(editRecDataBySite);

  const onSubmit = async (data: FormValues) => {
    const payload = {
      utilityTypeId: utilityTypes.find(
        (ut) => ut.name.toLowerCase() === data.utilityTypeName.toLowerCase()
      )?.id || 0,
      siteId: Number(site),
      utilityProvider: data.utilityProvider,
      country: data.country,
      renewableEnergy: data.renewableEnergy,
      startDate: checkNullValue(data.startDate?.format("YYYY-MM-DDT00:00:00[Z]")),
      endDate: checkNullValue(data.endDate?.format("YYYY-MM-DDT00:00:00[Z]")),
    };

    const onSuccess = () => {
      notify.success(`RECs have been successfully ${editREC ? "edited" : "added"}!`);
      queryClient.invalidateQueries([QUERY_KEYS.SITES]);
    }

    const mutation = editREC
      ? editRecDocument({ recId: editREC.recId, payload }, { onSuccess })
      : addRecDocument(payload, { onSuccess });

    try {
      const newRecResponseData = await mutation;
      if (newRecResponseData && newRecResponseData.data?.recId) navigate(`/recDetail/${newRecResponseData.data?.recId}?s=${site}`);
      onClose();
    } catch (error: any) {
      notify.error(error?.response?.data || `Error ${editREC ? "editing" : "adding"} REC!`);
    }
  };

  const customFieldsOrder = [
    "utilityProvider",
    "utilityTypeName",
    "country",
    "renewableEnergy",
    "startDate",
    "endDate",
  ];

  const sortedColumns = customFieldsOrder.map((key) =>
    columns?.find((column: any) => column.accessorKey === key)
  ).filter(Boolean);


  const helperTextWrapper = (errorMessage: string) =>
    <FormHelperText
      sx={{
        color: "red",
        fontFamily: "Manrope",
        fontSize: "11px",
        whiteSpace: "pre-wrap",
        overflow: "hidden",
        width: "240px",

        "&.MuiFormHelperText-root": {
          margin: "0px",
          marginLeft: "-11px",
          color: "red",
        },
      }}
    >
      {errorMessage}
    </FormHelperText>

  const renderField = (column: any) => (
    <Grid item xs={8} sm={6} key={column.header}>
      <Controller
        name={column.accessorKey as keyof FormValues}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <InputFieldWrapper
            label={column.accessorKey === "renewableEnergy" ? column.header : `${column.header} *`}
          >
            {["utilityProvider", "utilityTypeName", "country"].includes(column.accessorKey) ? (
              column.accessorKey === "utilityTypeName" ? (
                <Select
                  {...field}
                  disabled
                  sx={{ width: "100%", height: "36px" }}
                  value={field.value || ""}
                  error={!!error}
                >
                  {utilityTypes.map((type) => (
                    <MenuItem
                      key={type.id}
                      value={type.name}
                      sx={{
                        fontWeight: TYPOGRAPHY_COMMON_STYLES.fontWeightSemiBold,
                        fontSize: 14,
                        lineHeight: "18px",
                        color: "#90959E",
                        "&:hover": { backgroundColor: "#F6F8FA", borderRadius: 5, color: COLORS.Romance },
                        "&.Mui-selected": { backgroundColor: "#F6F8FA", borderRadius: 5, color: COLORS.Romance },
                      }}
                    >
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              ) : column.accessorKey === "country" ? (
                <Select
                  {...field}
                  disabled
                  sx={{ width: "100%", height: "36px" }}
                  value={field.value || ""}
                  error={!!error}
                >
                  {countriesAscending.map((country: string) => (
                    <MenuItem
                      key={country}
                      value={country}
                      sx={{
                        fontWeight: TYPOGRAPHY_COMMON_STYLES.fontWeightSemiBold,
                        fontSize: 14,
                        lineHeight: "18px",
                        color: "#90959E",
                        "&:hover": { backgroundColor: "#F6F8FA", borderRadius: 5, color: COLORS.Romance },
                        "&.Mui-selected": { backgroundColor: "#F6F8FA", borderRadius: 5, color: COLORS.Romance },
                      }}
                    >
                      {country}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <TextField
                  {...field}
                  size="small"
                  disabled
                  fullWidth
                  variant="outlined"
                  error={!!error}
                  helperText={helperTextWrapper(error?.message || '')}
                  sx={{ marginLeft: 0 }}
                />
              )
            ) : ["startDate", "endDate"].includes(column.accessorKey) ? (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  {...field}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: { width: "100%" },
                      error: !!error,
                      helperText: helperTextWrapper(error?.message || '')
                    },
                  }}
                  format={getDateFormatset()}
                />
              </LocalizationProvider>
            ) : (
              <TextField
                {...field}
                size="small"
                error={!!error}
                helperText={helperTextWrapper(error?.message || '')}
                fullWidth
                variant="outlined"
                sx={{ marginLeft: 0 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <span style={{ color: COLORS.Romance }}>%</span>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </InputFieldWrapper>
        )}
      />
    </Grid>
  );

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {editREC ? "REC Details" : "New REC Details"}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Grid container spacing={3}>
            {sortedColumns.map(renderField)}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container alignItems="center" justifyContent="flex-end" mb={2} ml={2}>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!isValid}
            >
              {editREC ? "Save" : "Add"}
            </Button>
          </Grid>
        </DialogActions>
      </form>
    </Dialog>
  );
};